import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { vendorCurrency } from '../constants';

function formatMoney(n, currency) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${Math.abs(v).toFixed(2)} ${currency}`;
}

function typeLabel(tx) {
  const t = String(tx?.transaction_type || '').toLowerCase();
  const extraType = String(tx?.extra_data?.type || '').toLowerCase();
  if (extraType === 'neighbour_order_commission') return 'Neighbour order';
  if (t === 'topup' || t === 'top_up' || t === 'credit') return 'Top-up';
  if (t === 'deduction' || t === 'debit' || t === 'charge') return 'Wallet fee';
  if (t === 'signup_bonus') return 'Signup bonus';
  if (t === 'bonus') return 'Bonus';
  if (t === 'ad' || t === 'ads') return 'Ads';
  return t ? t.replace(/_/g, ' ') : 'Transaction';
}

function isCredit(tx) {
  const t = String(tx?.transaction_type || '').toLowerCase();
  return (
    t === 'topup' ||
    t === 'top_up' ||
    t === 'credit' ||
    t === 'signup_bonus' ||
    t === 'bonus'
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
}

function cleanDescription(tx) {
  const raw = String(tx?.description || '').trim();
  if (!raw) return typeLabel(tx);
  // Strip legacy "% of GoSnel" style copy if any remain.
  return raw
    .replace(/\d+(\.\d+)?%\s*of\s*GoSnel[^.]*\.?/gi, '')
    .replace(/\d+(\.\d+)?%\s*of\s*[^.]*\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim() || typeLabel(tx);
}

export default function TransactionsPage() {
  const { vendor } = useAuth();
  const currency = vendorCurrency(vendor);
  const [balance, setBalance] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [balRes, txRes] = await Promise.all([
        api.get('/vendor/auth/finance/balance'),
        api.get('/vendor/auth/finance/transactions', { params: { limit: 50 } }),
      ]);
      setBalance(balRes.data?.balance ?? balRes.data?.wallet_balance ?? null);
      const list = Array.isArray(txRes.data?.transactions)
        ? txRes.data.transactions
        : Array.isArray(txRes.data)
          ? txRes.data
          : [];
      setRows(list);
    } catch (err) {
      setError(formatApiError(err, 'Could not load transactions'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Wallet top-ups and Neighbour order fees (with order totals).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/topup" variant="outlined" size="small">
            Top up
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size="small">
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: '#f0fdfa',
          borderColor: '#99f6e4',
        }}
      >
        <AccountBalanceWalletIcon sx={{ color: '#0f766e' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Wallet balance
          </Typography>
          <Typography fontWeight={900} sx={{ color: '#0f766e' }}>
            {balance == null ? '—' : `${Number(balance).toFixed(2)} ${currency}`}
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && !rows.length ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>
            No transactions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Top-ups and Neighbour order fees will show here.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.25}>
          {rows.map((tx) => {
            const credit = isCredit(tx);
            const fee = Number(tx.amount ?? tx.total_with_tax ?? 0);
            const orderTotal =
              tx.extra_data?.order_total != null
                ? Number(tx.extra_data.order_total)
                : null;
            const orderCode = tx.extra_data?.order_code || null;
            return (
              <Paper key={tx.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography fontWeight={800} noWrap>
                        {cleanDescription(tx)}
                      </Typography>
                      <Chip
                        size="small"
                        color={credit ? 'success' : 'default'}
                        label={typeLabel(tx)}
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      {formatDate(tx.date || tx.created_at)}
                      {orderCode ? ` · ${orderCode}` : ''}
                    </Typography>
                    {orderTotal != null && Number.isFinite(orderTotal) && (
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                        Order amount: {formatMoney(orderTotal, currency)}
                      </Typography>
                    )}
                    {tx.balance_after != null && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Balance after: {Number(tx.balance_after).toFixed(2)} {currency}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    fontWeight={900}
                    sx={{
                      color: credit ? '#15803d' : 'text.primary',
                      flexShrink: 0,
                    }}
                  >
                    {credit ? '+' : '−'}
                    {formatMoney(fee, currency)}
                  </Typography>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
